/**
 * Routes — All visits, Address book and the future areas (Compliance, Space
 * requests, Reception board) in the rail's order (concierge-desktop
 * screens/app-frame.md v0.4), plus Today and Add visit. Lazy via
 * loadComponent (default exports), titles via the route `title`. Unknown URLs
 * and the bare path redirect to All visits, the rail's first area. Every
 * staff page sits under the auth guard: platform mode requires a login-web session;
 * demo mode is an immediate no-op.
 *
 * Two trees sit outside the guard: the guest invitation (`/v/:customerId`)
 * and the design harness (`/preview/*`, development builds only; see
 * `preview/preview.routes.ts` and AGENTS.md).
 */

import { Routes } from '@angular/router';

import { ShellComponent } from './shell/shell.component';
import type { FutureArea } from './shell/future-area-page.component';

import { authGuard } from './core/auth.guard';
import { RDX_VISITORS_ENV } from './core/env';
import { PREVIEW_ENABLED } from './preview/preview-enabled';

/** Future areas: in the rail, not built yet. Each says so (shell/future-area-page). */
function futureAreas(...areas: readonly FutureArea[]): Routes {
  const titles: Record<FutureArea, string> = {
    compliance: $localize`:@@visitors-title-compliance:Compliance — Visitors · Workplace by Ricoh`,
    'space-requests': $localize`:@@visitors-title-space-requests:Space requests — Visitors · Workplace by Ricoh`,
    'reception-board': $localize`:@@visitors-title-reception-board:Reception board — Visitors · Workplace by Ricoh`,
  };
  return areas.map((area) => ({
    path: area,
    title: titles[area],
    data: { area },
    loadComponent: () => import('./shell/future-area-page.component'),
  }));
}

const pages: Routes = [
  {
    path: 'today',
    title: $localize`:@@visitors-title-today:Today — Visitors · Workplace by Ricoh`,
    loadComponent: () => import('./visits/today-page.component'),
  },
  {
    path: 'all-visits',
    title: $localize`:@@visitors-title-all-visits:All visits — Visitors · Workplace by Ricoh`,
    loadComponent: () => import('./visits/all-visits-page.component'),
  },
  {
    path: 'add-visit',
    title: $localize`:@@visitors-title-add-visit:Add visit — Visitors · Workplace by Ricoh`,
    loadComponent: () => import('./add-visit/add-visit-page.component'),
  },
  {
    path: 'address-book',
    title: $localize`:@@visitors-title-address-book:Address Book — Visitors · Workplace by Ricoh`,
    loadComponent: () => import('./address-book/address-book-page.component'),
  },
  ...futureAreas('compliance', 'space-requests', 'reception-board'),
  { path: '', pathMatch: 'full', redirectTo: 'all-visits' },
  { path: '**', redirectTo: 'all-visits' },
];

export const routes: Routes = [
  // Production builds swap PREVIEW_ENABLED to false, which drops this entry
  // (and its lazy chunks) at build time; canMatch is the runtime backstop.
  ...(PREVIEW_ENABLED
    ? [
        {
          path: 'preview',
          // Only a document opened on /preview (demo data, no network) may
          // route here; anything else falls through to the ** redirect.
          canMatch: [() => PREVIEW_ENABLED && RDX_VISITORS_ENV.PREVIEW_SESSION],
          loadChildren: () => import('./preview/preview.routes'),
        },
      ]
    : []),
  {
    path: 'v/:customerId',
    title: $localize`Your invitation`,
    loadComponent: () => import('./invitation/invitation-page.component'),
  },
  {
    path: '',
    component: ShellComponent,
    // Platform builds require auth on every page; in demo mode the guard is
    // an immediate no-op `true` (core/auth.guard.ts) — behaviour unchanged.
    canActivateChild: [authGuard],
    children: pages,
  },
];
